import {
  applyToPoint,
  densified,
  easedAt,
  invert,
  maxScale,
  nearestOnSegment,
  segmentsOf,
  pinchedAt,
  ROOT_ID,
  spikedAt,
  subpathsOfCommands,
  twirledAt,
  warpedAt,
  wrinkledAt,
  nodeCommands,
  newId,
  type NodeRecord,
  type PathPoint,
  type Scene,
  type Subpath, replaceWithPathOps } from '@flobi/artwork-core';
import type { IconName } from '../chrome/icons';
import type { Point, RailButton, Tool, ToolContext } from './types';

/**
 * Liquify: pushing a shape about with a brush.
 *
 * Illustrator keeps seven of these behind one flyout — warp, twirl, pucker, bloat,
 * scallop, crystallize, wrinkle — and they are seven marks for one gesture: hold a round
 * brush over an outline and drag. So they are the rail's top well, the way the pencil's
 * five are, and the one quantity each of them has is below.
 *
 * Every one of them adds anchors as it goes, because none of them can work without. A
 * rectangle has four corners and nothing in between; bloating it without adding anchors
 * moves the four corners and leaves the sides straight, which is not bloating, it is
 * dragging. `densified` puts anchors under the brush first, and only under the brush —
 * the rest of the shape is left as simple as it was drawn.
 */

export type Mode = 'warp' | 'twirl' | 'pucker' | 'bloat' | 'scallop' | 'crystallize' | 'wrinkle';

/**
 * The seven, shared with the Width tool, which shows the same family in its own rail.
 *
 * Illustrator keeps Width and the liquify seven behind one flyout, and a person looks in
 * one place for all eight. Two tools, one list — so whichever is in hand, the rail offers
 * the other seven and pressing one puts it in hand.
 */
export const BRUSHES: { id: Mode; icon: IconName; title: string; panelGroup: string }[] = [
  { id: 'warp', icon: 'warp', panelGroup: 'Reshape', title: 'Warp — Drag over an outline to push it.' },
  { id: 'twirl', icon: 'twirl', panelGroup: 'Reshape', title: 'Twirl — Hold over an outline to twist it around the brush.' },
  { id: 'pucker', icon: 'pucker', panelGroup: 'Reshape', title: 'Pinch — Pull the outline toward the brush center.' },
  { id: 'bloat', icon: 'bloat', panelGroup: 'Reshape', title: 'Swell — Push the outline away from the brush center.' },
  { id: 'scallop', icon: 'scallop', panelGroup: 'Edge texture', title: 'Scallop — Add rounded notches along an outline.' },
  { id: 'crystallize', icon: 'star', panelGroup: 'Edge texture', title: 'Crystallize — Add outward spikes along an outline.' },
  { id: 'wrinkle', icon: 'wrinkle', panelGroup: 'Edge texture', title: 'Wrinkle — Add small, uneven folds along an outline.' },
];

/** Which brush the liquify tool comes back to, kept where the Width rail can set it. */
let chosen: Mode = 'warp';
export const brushChosen = (mode: Mode): void => {
  chosen = mode;
};

/** How often a held brush works on while the hand holds still, in milliseconds. */
const HOLD_EVERY = 40;
/**
 * The longest one pass may count for, in seconds. A pass is measured by the time since
 * the one before it, and a tab that was hidden or a frame that stalled would otherwise
 * arrive as one enormous shove.
 */
const LONGEST_PASS = 0.1;
/**
 * How much each brush does in a second of leaning at full intensity.
 *
 * Per SECOND, not per pass (QA 2026-09-25, liquify-N1). These were tuned "per frame, and
 * a held brush is thirty frames a second" — but only twirl had anything running while the
 * hand held still, and every other brush worked once per pointer event: holding it did
 * nothing, and how strong a stroke came out depended on how many move events the mouse
 * happened to send. Said per second and multiplied by the time a pass stands for, the
 * same gesture does the same thing with any mouse, and holding still keeps working.
 * Tuned so a strong brush arrives somewhere in half a second: a full-intensity swell
 * nearly doubles how far an anchor near the middle sits from it in that time.
 */
const RATE = { twirl: 1.5, pinch: 1.2, teeth: 1.2 } as const;
/**
 * How far apart the anchors a brush lays down are, as a share of the brush's radius — and
 * never closer than FINEST screen pixels, which no eye can tell apart.
 *
 * A quarter of the radius left four anchors across a brush whose push changes all the way
 * across it, so a smear came out as a few long pieces meeting at points (Tarek,
 * 2026-10-01: "the edges are very choppy… we need more points no?"). Twice as fine, and
 * the anchors are eased smooth as they are bent (`easedAt`).
 */
const STEP = 0.12;
const FINEST = 2;
/**
 * The most anchors one subpath is worth carrying.
 *
 * A brush works per frame, and every frame it asks for anchors under itself. Held down
 * for a second that is thirty rounds of asking, and without a ceiling a shape someone
 * leant on comes out with ten thousand points — unmovable, unsavable, and impossible to
 * edit by hand afterwards. Past this the brush stops adding and works with what is there,
 * which is what a person leaning on it actually wants: deeper, not denser.
 */
const MOST_POINTS = 1500;

/**
 * Ops that point every cloner and effector that followed `from` at `to` instead.
 *
 * Turning a rectangle into a path gives it a new id, and a cloner strung along it or an
 * effector shaped by it named the old one — so the copies vanished and the effector went
 * dead the moment a brush touched the shape (QA 2026-09-25, liquify-F7). Run in the same
 * batch as the swap, so the link never points at nothing. Forced, because keeping a
 * locked cloner on its path is not an edit to the cloner.
 */
export function relinked(scene: Scene, from: string, to: string): unknown[] {
  const ops: unknown[] = [];
  for (const id of scene.ids()) {
    const node = scene.get(id) as { pathId?: string; shapeId?: string } | undefined;
    if (node?.pathId === from) ops.push({ op: 'set', id, props: { pathId: to }, force: true });
    if (node?.shapeId === from) ops.push({ op: 'set', id, props: { shapeId: to }, force: true });
  }
  return ops;
}

/**
 * A layer turned into a path, keeping everything about it and every link to it: the
 * swap `replaceWithPathOps` makes, plus `relinked`, in one batch. The new path's id is
 * chosen here, before the batch, because a batch cannot name what one of its own ops is
 * about to create inside another op's fields. Answers the new id, the same id for a
 * path, or null when the layer may not be converted or the edit failed.
 */
export function convertedToPath(ctx: ToolContext, id: string, subpaths: Subpath[]): string | null {
  const node = ctx.node(id);
  if (!node) return null;
  const made = node.type === 'path' ? id : newId();
  const ops = replaceWithPathOps(ctx.scene, id, { subpaths }, node.type === 'path' ? {} : { props: { id: made } });
  if (!ops) return null;
  const chosen = [...ctx.selection];
  const result = ctx.edit(made === id ? ops : [...ops, ...relinked(ctx.scene, id, made)]);
  if (!result.ok) return null;
  // A chosen shape stays chosen as the path it now is. The selection went on naming the
  // old id, which no longer exists, so the shape brushed was dropped from it
  // (QA 2026-09-25, liquify-F13).
  if (made !== id && chosen.includes(id)) ctx.setSelection(chosen.map((each) => (each === id ? made : each)).filter((each) => ctx.scene.has(each)));
  return made;
}

export function liquifyTool(): Tool {
  const settings = { brush: 60, intensity: 50, detail: 30 };
  const modeOf = () => chosen;
  /** Where the pointer is, so the brush can be drawn at its size before it is used. */
  let hover: Point | null = null;
  let last: Point | null = null;
  let pressing = false;
  /** Which anchors the teeth fall on: chosen once per press, so a held brush deepens. */
  let phase = 0;
  let winding: ReturnType<typeof setInterval> | null = null;
  /** When the brush last worked, so the next pass knows how much time it stands for. */
  let lastPass = 0;
  /** The seconds since the last pass, clamped, and the clock moved on. */
  const elapsed = (): number => {
    const now = performance.now();
    const seconds = Math.max(0, Math.min(LONGEST_PASS, (now - lastPass) / 1000));
    lastPass = now;
    return seconds;
  };

  const radiusOf = (ctx: ToolContext) => (settings.brush / 2) * ctx.unitsPerPixel;
  /** 0.02 to 1, from Intensity: a brush with nothing behind it does nothing, which is a useless brush. */
  const strength = () => Math.max(0.02, settings.intensity / 100);

  const stopWinding = () => {
    if (winding !== null) clearInterval(winding);
    winding = null;
  };

  /** A node's outline, whether it is stored as one or worked out from what it is. */
  const outlineOf = (node: NodeRecord): Subpath[] => {
    if (node.type === 'path') return (node.path?.subpaths ?? []) as Subpath[];
    const commands = nodeCommands(node);
    return commands ? subpathsOfCommands([...commands]) : [];
  };

  /** Where the brush is in a node's own coordinates, and how wide it is there. */
  const reaching = (ctx: ToolContext, id: string, world: Point) => {
    const back = invert(ctx.scene.worldTransform(id));
    if (!back) return null;
    // The brush is a size on the screen; the path is in its own coordinates, which may be
    // scaled by every group it sits in. One number converts between them.
    const shrink = maxScale(ctx.scene.worldTransform(id)) || 1;
    return { at: applyToPoint(back, world.x, world.y), radius: radiusOf(ctx) / shrink, shrink };
  };

  /**
   * Whether the brush is actually over a shape's OUTLINE, rather than merely over its box.
   *
   * A box is a poor question and this is what it costs: a brush anywhere inside a long
   * diagonal shape's box counts as touching it, so at a low zoom, where the brush is
   * hundreds of document units across, one press reaches half the page. Everything it
   * reached was turned into a path and pushed about — and nothing about the press said
   * that would happen. Asking the outline instead is a handful of distances, and it is
   * the difference between a brush and a blast radius.
   */
  const touches = (ctx: ToolContext, id: string, world: Point): boolean => {
    const node = ctx.node(id);
    const near = node && reaching(ctx, id, world);
    if (!node || !near) return false;
    for (const subpath of outlineOf(node)) {
      for (const segment of segmentsOf(subpath.points, subpath.closed)) {
        if (nearestOnSegment(segment, near.at).distance <= near.radius) return true;
      }
    }
    return false;
  };

  /**
   * The paths the brush is over — as paths, whatever they were drawn as.
   *
   * A rectangle is four corners and a width; there is nowhere on it to put the anchors a
   * brush needs. Illustrator has the same problem and answers it the same way: the first
   * touch turns the shape into the outline of itself, and from then on it is a path like
   * any other. It is the honest trade — a rectangle that could be liquified and still
   * have a width field would be lying about one of the two. Which is exactly why it must
   * only happen to shapes the brush is really on.
   */
  const under = (ctx: ToolContext, world: Point): string[] => {
    const reach = radiusOf(ctx);
    const box = { x: world.x - reach, y: world.y - reach, width: reach * 2, height: reach * 2 };
    const out: string[] = [];
    for (const id of ctx.nearby(box)) {
      const node = ctx.node(id);
      if (!node || node.locked || node.type === 'artboard' || node.type === 'text' || node.type === 'image') continue;
      if (!touches(ctx, id, world)) continue;
      if (node.type === 'path') {
        out.push(id);
        continue;
      }
      const made = outlined(ctx, id);
      if (made) out.push(made);
    }
    return out;
  };

  /**
   * A shape rewritten as the path that draws it, keeping everything else about it — its
   * effects, blend mode, animation and place in the stack included (replaceWithPathOps).
   * Pictures, text and containers are left alone rather than flattened.
   */
  const outlined = (ctx: ToolContext, id: string): string | null => {
    const node = ctx.node(id);
    if (!node) return null;
    const commands = nodeCommands(node);
    const subpaths = commands ? subpathsOfCommands([...commands]) : [];
    if (subpaths.length === 0) return null;
    // Cloners strung along it and effectors shaped by it follow it to its new id.
    return convertedToPath(ctx, id, subpaths);
  };

  /**
   * One pass of the brush over one path.
   *
   * Every subpath, not just the first: a painted blob is an outline and its holes, and a
   * brush that bloated the outline but not the hole would tear the shape open.
   */
  const brush = (ctx: ToolContext, id: string, world: Point, by: Point, grow: boolean, seconds: number): void => {
    const node = ctx.node(id);
    if (node?.type !== 'path' || !node.path?.subpaths?.length) return;
    const near = reaching(ctx, id, world);
    if (!near) return;
    const { at, radius, shrink } = near;
    const step = Math.max(radius * STEP, (FINEST * ctx.unitsPerPixel) / shrink);
    // The four that push bend the outline, and what they bend is eased smooth; the three
    // that cut teeth leave corners on purpose.
    const pushing = modeOf() === 'warp' || modeOf() === 'twirl' || modeOf() === 'pucker' || modeOf() === 'bloat';
    // The hand's drag, turned into the shape's own axes: only scaling it left a rotated
    // or flipped shape pushed the wrong way (QA 2026-09-24, liquify-F6).
    const placed = ctx.scene.worldTransform(id);
    const inverse = invert(placed);
    const drag = inverse ? { x: inverse[0] * by.x + inverse[2] * by.y, y: inverse[1] * by.x + inverse[3] * by.y } : by;
    mirrored = placed[0] * placed[3] - placed[1] * placed[2] < 0;

    const subpaths: Subpath[] = node.path.subpaths.map((subpath) => {
      const room = Math.max(0, MOST_POINTS - subpath.points.length);
      const dense = grow && room > 0 ? densified(subpath.points, subpath.closed, at, radius, step, room) : [...subpath.points];
      const moved = pushed(dense, subpath.closed, at, radius, drag, seconds);
      return { closed: subpath.closed, points: pushing ? easedAt(dense, moved, subpath.closed) : moved };
    });
    ctx.edit([{ op: 'set', id, props: { path: { fillRule: node.path.fillRule ?? 'nonzero', subpaths } } }]);
  };

  /** Whether the shape being brushed is mirrored on the page. */
  let mirrored = false;

  /**
   * Which of the seven, applied. The only place the modes differ.
   *
   * Warp goes as far as the hand went. Every other one goes by `seconds`, the time this
   * pass stands for — see RATE — so what a stroke does is how long it leaned, however
   * many events carried it.
   *
   * The three that cut teeth go by Detail alone, the one control their strip shows. They
   * were scaled by Intensity too, which is on screen only for the other four, so lowering
   * it for Pinch left Scallop cutting tiny teeth with nothing to say why (QA 2026-09-25,
   * liquify-F9). RATE.teeth was halved with it, so at the default Intensity (50) the teeth
   * come out as deep as they did.
   */
  const pushed = (points: PathPoint[], closed: boolean, at: Point, radius: number, by: Point, seconds: number): PathPoint[] => {
    const power = strength();
    const detail = settings.detail / 100;
    switch (modeOf()) {
      case 'warp':
        return warpedAt(points, at, radius, by, power);
      case 'twirl':
        // A mirrored shape turns the other way in its own space, so it is turned back.
        return twirledAt(points, at, radius, power * RATE.twirl * seconds * (mirrored ? -1 : 1));
      case 'pucker':
        return pinchedAt(points, at, radius, -power * RATE.pinch * seconds);
      case 'bloat':
        return pinchedAt(points, at, radius, power * RATE.pinch * seconds);
      case 'scallop':
        return spikedAt(points, closed, at, radius, -detail * RATE.teeth * seconds, phase);
      case 'crystallize':
        return spikedAt(points, closed, at, radius, detail * RATE.teeth * seconds, phase);
      case 'wrinkle': {
        const reach = radius * detail * RATE.teeth * seconds;
        return wrinkledAt(points, at, radius, reach, reach, phase);
      }
    }
  };

  /**
   * One pass of the brush. `grow` says whether it may add anchors as well as move them.
   *
   * Only where the brush has arrived somewhere new. Held still, a brush asking for more
   * anchors thirty times a second buries the ones it already pushed under fresh ones
   * between them — a scallop cut its teeth on different anchors every frame, so what came
   * out was a roughened edge rather than teeth, and the point count climbed for nothing.
   * Held on one set, the same teeth deepen, which is what holding a brush means.
   */
  const pass = (ctx: ToolContext, world: Point, by: Point, grow: boolean) => {
    const seconds = elapsed();
    for (const id of under(ctx, world)) brush(ctx, id, world, by, grow, seconds);
    ctx.refresh();
  };

  return {
    id: 'liquify',

    deactivate(ctx) {
      stopWinding();
      pressing = false;
      hover = null;
      last = null;
      ctx.setCursor(null);
    },

    onPointerDown(_event, world, ctx) {
      pressing = true;
      last = world;
      // Once a press, not once a frame. The teeth a scallop cuts and the wobble a wrinkle
      // puts in are chosen by the phase, so moving it per frame had every frame cutting
      // somewhere else and a held brush turning the outline to scribble. Held on one
      // phase, the same teeth deepen — which is what scalloping something looks like.
      phase += 1;
      // The press itself counts as one tick of leaning, so a quick tap still does a little.
      lastPass = performance.now() - HOLD_EVERY;
      pass(ctx, world, { x: 0, y: 0 }, true);
      // Every brush but warp goes on working while the hand holds still: twirl winds,
      // swell swells, the teeth deepen. Warp alone is a smear, and a smear with the hand
      // still is nothing. Only twirl had this, so the other five did nothing held
      // (QA 2026-09-25, liquify-N1).
      stopWinding();
      if (modeOf() !== 'warp') {
        // A held twirl, pinch or swell stretches the outline as it goes, and a stretched
        // piece with no anchors in it is a straight line across the curve; so those three
        // go on laying anchors, where the pieces under them have grown longer than a step
        // (`densified` adds none to a piece already that fine). The three that cut teeth do
        // not: a tooth cut on fresh anchors every pass is a roughened edge, not teeth.
        const teeth = modeOf() === 'scallop' || modeOf() === 'crystallize' || modeOf() === 'wrinkle';
        winding = setInterval(() => {
          if (pressing && last) pass(ctx, last, { x: 0, y: 0 }, !teeth);
        }, HOLD_EVERY);
      }
      return true;
    },

    onPointerMove(event, world, ctx) {
      hover = world;
      if (event.buttons === 0 || !pressing) {
        last = null;
        ctx.refresh();
        return;
      }
      const from = last ?? world;
      const by = { x: world.x - from.x, y: world.y - from.y };
      pass(ctx, world, by, Math.hypot(by.x, by.y) > ctx.unitsPerPixel);
      last = world;
    },

    /**
     * Off the canvas the brush circle goes: it stayed drawn where the pointer left, over
     * the dock or a panel (QA 2026-09-25, liquify-F16). Mid-press the pointer is captured,
     * so this comes only once the hand is up or truly gone.
     */
    onPointerLeave(ctx) {
      hover = null;
      ctx.refresh();
    },

    onPointerUp(_event, _world, ctx) {
      stopWinding();
      pressing = false;
      last = null;
      // One press of the brush is one thing to take back, however many frames it took.
      ctx.stopCapturing();
    },

    /**
     * The seven above, and what the one in hand is measured by below.
     *
     * Two quantities for the three that cut detail into an edge, because how deep the
     * teeth go and how many of them there are is genuinely two questions — which is the
     * same reason the star has two.
     */
    rail(ctx) {
      const items: RailButton[] = [
        {
          id: 'width',
          icon: 'width' as const,
          title: 'Width — Drag away from a path to thicken its stroke, or inward to thin it.',
          panelGroup: 'Stroke',
          pressed: false,
          run: () => ctx.chooseTool('width'),
        },
        ...BRUSHES.map((item) => ({
        id: item.id,
        icon: item.icon,
        title: item.title,
        panelGroup: item.panelGroup,
        pressed: modeOf() === item.id,
        run: () => {
          brushChosen(item.id);
          ctx.railChanged();
        },
        })),
      ];
      // The three that cut teeth are measured by how deep the teeth go; the four that
      // push are measured by how hard they push. Two names for the middle well because
      // they are two questions, and neither tool has both.
      const mode = modeOf();
      const detailed = mode === 'scallop' || mode === 'crystallize' || mode === 'wrinkle';
      return {
        top: { kind: 'buttons' as const, items },
        middle: detailed
          ? {
              kind: 'slider' as const,
              icon: 'star' as const,
              title: 'Detail',
              value: settings.detail,
              min: 5,
              max: 100,
              onChange: (value: number) => {
                settings.detail = value;
                ctx.railChanged();
              },
            }
          : {
              kind: 'slider' as const,
              icon: 'weight' as const,
              title: 'Intensity',
              value: settings.intensity,
              min: 5,
              max: 100,
              onChange: (value: number) => {
                settings.intensity = value;
                ctx.railChanged();
              },
            },
        bottom: {
          kind: 'slider' as const,
          icon: 'opacity' as const,
          title: 'Brush size',
          value: settings.brush,
          min: 10,
          max: 300,
          onChange: (value: number) => {
            settings.brush = value;
            ctx.railChanged();
          },
        },
      };
    },

    /**
     * The brush, where the pointer is, at the size it will work at.
     *
     * A liquify brush is a reach rather than a mark — nothing is laid down, things nearby
     * are moved — so the only way to know what a press will touch is to see the circle.
     * Illustrator draws it for the same reason, and its brush is the cursor.
     */
    draw(overlay, ctx) {
      if (!hover) return;
      const radius = radiusOf(ctx);
      const steps = 32;
      overlay.curve(
        Array.from({ length: steps + 1 }, (_, step) => {
          const angle = (step / steps) * Math.PI * 2;
          return { at: { x: hover!.x + Math.cos(angle) * radius, y: hover!.y + Math.sin(angle) * radius } };
        }),
        { width: 1 },
      );
    },
  };
}
